import AgencyNavbar from "@/components/agency/AgencyNavbar";
import AgencyFooter from "@/components/agency/AgencyFooter";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import { ArrowRight, RefreshCw, MapPin, Star, PhoneCall, ShieldCheck, QrCode, LineChart, Layout, Search, Calendar, CheckCircle2 } from "lucide-react";
import { cities, cityPath, tradePath, trades } from "@/lib/localSeo";
import websitesHero from "@/assets/agency/contractor-website-in-use.jpg";

const services = [
  {
    icon: Layout,
    label: "Custom web design and build",
    desc: "A site built from scratch around your services, service area, and the jobs you actually want. Mobile first, fast, and written to get people to call.",
  },
  {
    icon: RefreshCw,
    label: "Website redesign and rebuild",
    desc: "Already have a site that looks dated or never brings in work? I rebuild it, keep the pages that rank, and redirect everything else properly.",
  },
  {
    icon: MapPin,
    label: "Local SEO for King County",
    desc: "Keyword research, city and service pages, internal links, and schema so you show up when nearby homeowners search. Search Console monitoring starts with Growth.",
  },
  {
    icon: Star,
    label: "Google Business Profile management",
    desc: "Services, categories, hours, and photos kept current, Q&A written out, and every review answered by me in your voice.",
  },
  {
    icon: PhoneCall,
    label: "Lead capture and conversion setup",
    desc: "Click to call, quote forms, photo uploads, booking links, and follow-up notifications so leads land in your phone and inbox right away.",
  },
  {
    icon: ShieldCheck,
    label: "Hosting, maintenance, and security",
    desc: "Hosting, backups, platform updates, uptime and speed checks, plus ongoing access to me for content and design changes.",
  },
  {
    icon: QrCode,
    label: "Digital business card and QR code",
    desc: "A tap-to-call card page with a scannable code for your truck, yard signs, estimates, and printed cards. Included from the Growth plan up.",
  },
  {
    icon: LineChart,
    label: "Tracking and reporting",
    desc: "Search Console, analytics, and form and call tracking wired up, with ranking and lead reviews on the Growth Partner plan.",
  },
];

const starterSeo = [
  "Keyword research for your core services and towns",
  "Title tags, meta descriptions, and heading structure on every page",
  "Image alt text and clean, readable page URLs",
  "Local business and service schema markup",
  "Google Business Profile connected and cleaned up",
  "Sitemap prepared for search engines, with Search Console setup available from Growth",
  "Speed, mobile, and broken-link checks before launch",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Seattle Contractor Web Design",
  provider: { "@type": "LocalBusiness", name: "jaik" },
  areaServed: { "@type": "AdministrativeArea", name: "King County, WA" },
  description: "Custom high-converting websites for Seattle contractors. Roofing, remodeling, plumbing, gutters, HVAC.",
};

const ContractorWebsites = () => {
  return (
    <div className="min-h-screen bg-agency-bg font-sans text-agency-text">
      <SEOHead
        title="Contractor Web Design | Seattle & King County, WA | jaik"
        description="Contractor web design for Seattle and King County trades. Roofing, plumbing, HVAC and remodeling sites built to rank locally and bring in calls. Starter SEO included."
        canonical="https://jaikmarketing.com/contractor-websites"
        ogImage="/og-contractor-websites.jpg"
        jsonLd={jsonLd}
      />
      <AgencyNavbar />

      <main>
        {/* Hero */}
        <section className="relative min-h-[650px] sm:min-h-[700px] flex items-end overflow-hidden">
          <img src={websitesHero} alt="Contractor checking his website on a phone at the tailgate of a work truck" width={1536} height={960} className="absolute inset-0 h-full w-full object-cover object-[68%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-agency-bg via-agency-bg/90 to-agency-bg/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-agency-bg via-transparent to-agency-bg/30" />
          <div className="container relative z-10 pb-14 pt-32 sm:pb-20 sm:pt-40">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
              <span className="font-sans text-xs text-agency-accent tracking-[0.2em] uppercase mb-4 block">Services</span>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Contractor Web Design for Roofing, Plumbing and Remodeling
              </h1>
              <p className="font-sans text-agency-text-muted text-lg sm:text-xl leading-relaxed mb-8">
                 Custom websites built to turn King County searches into calls and quote requests.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book" className="btn-jaik btn-jaik-primary btn-jaik-lg">
                  <Calendar className="h-4 w-4" /> Book a Strategy Call
                </Link>
                <Link to="/pricing" className="btn-jaik btn-jaik-secondary btn-jaik-lg">
                  See packages and pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What I do */}
        <section className="py-16 sm:py-24">
          <div className="container">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4">
              What I do for contractors
            </h2>
            <p className="font-sans text-agency-text-muted mb-12 max-w-2xl">
              Everything here is work I do myself. No outsourced teams, no reselling someone else's dashboard.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((n, i) => (
                <motion.div key={n.label} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
                  className="bg-agency-surface border border-agency-border rounded-xl p-6 hover:border-agency-accent/30 transition-all">
                  <n.icon className="h-8 w-8 text-agency-accent mb-4" />
                  <h3 className="font-heading text-lg font-semibold mb-2">{n.label}</h3>
                  <p className="font-sans text-sm text-agency-text-muted leading-relaxed">{n.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Starter SEO */}
        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container">
            <div className="max-w-4xl mx-auto bg-agency-surface border border-agency-border rounded-2xl p-7 sm:p-10">
              <span className="font-sans text-xs text-agency-accent tracking-[0.2em] uppercase mb-3 block">Included with every website</span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Starter SEO, included with every site I build
              </h2>
              <p className="font-sans text-agency-text-muted leading-relaxed mb-8 max-w-2xl">
                A new site is worth nothing if nobody finds it. Every build launches with real search work already done, not as a paid add-on later. Ongoing plans build on top of this every month with new pages, deeper keyword work, and technical fixes.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {starterSeo.map((item) => (
                  <li key={item} className="flex gap-3 font-sans text-sm text-agency-text-muted leading-relaxed">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-agency-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/contractor-seo" className="btn-jaik btn-jaik-secondary">
                  See how the ongoing SEO works <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal links: trades and King County cities */}
        <section className="py-16 sm:py-20 border-t border-agency-border">
          <div className="container">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Websites by trade</h2>
            <p className="font-sans text-agency-text-muted mb-6 max-w-2xl">
              Each trade gets its own page structure, keywords, and lead capture.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-14">
              {trades.map((t) => (
                <Link key={t.slug} to={tradePath(t)} className="bg-agency-surface/60 border border-agency-border rounded-xl px-5 py-4 hover:border-agency-accent/40 transition-colors font-sans text-sm font-medium">
                  {t.label} contractor web design
                </Link>
              ))}
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Websites by King County city</h2>
            <p className="font-sans text-agency-text-muted mb-6 max-w-2xl">
              Local pages written for how homeowners in each city search.{" "}
              <Link to="/king-county-contractors" className="text-agency-accent underline underline-offset-4">See every city</Link>.
            </p>
            <div className="flex flex-wrap gap-2.5">
              {cities.map((c) => (
                <Link key={c.slug} to={cityPath(c)} className="rounded-full border border-agency-border bg-agency-surface/60 px-4 py-2 text-sm text-agency-text-muted hover:text-agency-text hover:border-agency-accent/40 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA → Pricing */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-agency-accent/8 via-agency-bg to-agency-surface" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-agency-accent/5 rounded-full blur-[100px]" />
          <div className="container relative z-10 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-6">
              Ready to Get More Jobs?
            </h2>
            <p className="font-sans text-agency-text-muted text-lg mb-8 max-w-xl mx-auto">
              Book a free strategy call and we'll design a custom homepage mockup for your contracting business, with no commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book" className="btn-jaik btn-jaik-primary btn-jaik-lg">
                <Calendar className="h-4 w-4" /> Book a Strategy Call
              </Link>
              <Link to="/pricing" className="btn-jaik btn-jaik-secondary btn-jaik-lg">
                See packages and pricing <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <AgencyFooter />
    </div>
  );
};

export default ContractorWebsites;
